import React, { useState, useEffect, useRef, useContext } from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import {
  FaTicketAlt,
  FaChartBar,
  FaCalendar,
  FaPercentage,
  FaFutbol,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaHome,
  FaPlane,
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_DAY_OFFSET = -180;
const MAX_DAY_OFFSET = 2;

export default function Premium() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const userTier = user?.subscription?.tier || "trial";

  const [valueBets, setValueBets] = useState([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDayOffset, setSelectedDayOffset] = useState(0);
  const [dayStats, setDayStats] = useState({ total: 0, won: 0, lost: 0 });
  const [upcomingMatchesCount, setUpcomingMatchesCount] = useState(0);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sortBy, setSortBy] = useState("value");

  // State pentru filtrul avansat
  const [selectedMarket, setSelectedMarket] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Goals Over - checkbox-uri
  const [selectedGoalsOver, setSelectedGoalsOver] = useState({
    over1_5: false,
    over2_5: false,
    overHome: {
      over0_5: false,
      over1_5: false,
    },
    overAway: {
      over0_5: false,
      over1_5: false,
    },
  });

  // Goals Under - checkbox-uri
  const [selectedGoalsUnder, setSelectedGoalsUnder] = useState({
    under2_5: false,
    under3_5: false,
    under4_5: false,
    underHome: {
      under2_5: false,
      under3_5: false,
    },
    underAway: {
      under2_5: false,
      under3_5: false,
    },
  });

  // Simple filters (fără submeniu) - doar ON/OFF
  const [selectedBts, setSelectedBts] = useState(false);
  const [selectedMatchWinner, setSelectedMatchWinner] = useState(false);

  // State pentru expandarea meniurilor
  const [expandedMenus, setExpandedMenus] = useState({
    status: false,
    goalsOver: false,
    goalsUnder: false,
  });

  const calendarRef = useRef(null);

  const getTodayFormatted = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    return `${year}-${month}-${day}`;
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const getDayDisplayName = (offset) => {
    if (offset === 0) return "Today";
    const d = new Date();
    d.setDate(d.getDate() + offset);
    const dayName = d.toLocaleDateString(undefined, { weekday: "long" });
    const dateStr = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${dayName}, ${dateStr}`;
  };

  const calculateMargin = (odd, winProbability) => {
    const oddNum = typeof odd === "string" ? parseFloat(odd) : odd || 0;
    const winProbNum =
      typeof winProbability === "string"
        ? parseFloat(winProbability)
        : winProbability || 0;

    if (!oddNum || !winProbNum) return 0;

    const impliedProbability = 100 / oddNum;
    return (winProbNum - impliedProbability).toFixed(1);
  };

  const calculateBreakEven = (odd) => {
    const oddNum = typeof odd === "string" ? parseFloat(odd) : odd || 0;
    if (!oddNum) return 0;
    return (100 / oddNum).toFixed(1);
  };

  const getCardBorder = (matchOutcome, matchStatus) => {
    if (matchOutcome === "won") return "border-green-500 border-2";
    if (matchOutcome === "lost") return "border-red-500 border-2";
    if (matchOutcome === "void") return "border-gray-500 border";
    if (matchStatus === "published") return "border-blue-400 border-2";
    return "border-gray-700 border";
  };

  const getStatusBadge = (status, outcome) => {
    if (outcome === "won") {
      return (
        <span className="px-2 py-1 rounded text-xs text-white bg-green-600">
          WON
        </span>
      );
    }
    if (outcome === "lost") {
      return (
        <span className="px-2 py-1 rounded text-xs text-white bg-red-500">
          LOST
        </span>
      );
    }
    if (outcome === "void") {
      return (
        <span className="px-2 py-1 rounded text-xs text-white bg-gray-600">
          VOID
        </span>
      );
    }

    const statusConfig = {
      draft: { color: "bg-gray-500", text: "Draft" },
      published: { color: "bg-blue-500", text: "Published" },
      won: { color: "bg-green-600", text: "WON" },
      lost: { color: "bg-red-500", text: "LOST" },
      void: { color: "bg-gray-600", text: "VOID" },
    };
    const config = statusConfig[status] || {
      color: "bg-gray-400",
      text: status,
    };
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const determineMatchOutcome = (match) => {
    if (match.outcome && match.outcome !== "pending") return match.outcome;

    const isMatchFinished =
      match.match_status &&
      ["FT", "AET", "PEN", "FT_PEN"].includes(match.match_status);

    if (isMatchFinished) {
      return "pending";
    }

    return "pending";
  };

  const isMatchUpcoming = (match) => {
    const outcome = determineMatchOutcome(match);
    if (outcome === "won" || outcome === "lost" || outcome === "void") {
      return false;
    }

    const matchStatus = match.match_status;

    if (
      matchStatus &&
      ["FT", "AET", "PEN", "FT_PEN", "CANCL", "POSTP", "ABAN"].includes(
        matchStatus,
      )
    ) {
      return false;
    }

    return true;
  };

  // Funcții pentru a verifica tipul de pariat
  const isOverTotal = (bet, value) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const hasOver = marketType.includes("over") || selection.includes("over");
    const hasUnder =
      marketType.includes("under") || selection.includes("under");
    const hasValue = marketType.includes(value) || selection.includes(value);
    const hasHome = marketType.includes("home") || selection.includes("home");
    const hasAway = marketType.includes("away") || selection.includes("away");

    const hasGoalsOverUnder =
      marketType.includes("goals over/under") ||
      selection.includes("goals over/under");

    if (hasGoalsOverUnder) {
      const selectionLower = selection.toLowerCase();
      return (
        selectionLower.includes("over") &&
        selectionLower.includes(value) &&
        !selectionLower.includes("under")
      );
    }

    return hasOver && !hasUnder && hasValue && !hasHome && !hasAway;
  };

  const isUnderTotal = (bet, value) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const hasUnder =
      marketType.includes("under") || selection.includes("under");
    const hasOver = marketType.includes("over") || selection.includes("over");
    const hasValue = marketType.includes(value) || selection.includes(value);
    const hasHome = marketType.includes("home") || selection.includes("home");
    const hasAway = marketType.includes("away") || selection.includes("away");

    const hasGoalsOverUnder =
      marketType.includes("goals over/under") ||
      selection.includes("goals over/under");

    if (hasGoalsOverUnder) {
      const selectionLower = selection.toLowerCase();
      return (
        selectionLower.includes("under") &&
        selectionLower.includes(value) &&
        !selectionLower.includes("over")
      );
    }

    return hasUnder && !hasOver && hasValue && !hasHome && !hasAway;
  };

  const isOverHome = (bet, value) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const hasOver = marketType.includes("over") || selection.includes("over");
    const hasUnder =
      marketType.includes("under") || selection.includes("under");
    const hasHome = marketType.includes("home") || selection.includes("home");
    const hasValue = marketType.includes(value) || selection.includes(value);

    return hasOver && !hasUnder && hasHome && hasValue;
  };

  const isOverAway = (bet, value) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const hasOver = marketType.includes("over") || selection.includes("over");
    const hasUnder =
      marketType.includes("under") || selection.includes("under");
    const hasAway = marketType.includes("away") || selection.includes("away");
    const hasValue = marketType.includes(value) || selection.includes(value);

    return hasOver && !hasUnder && hasAway && hasValue;
  };

  const isUnderHome = (bet, value) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const hasUnder =
      marketType.includes("under") || selection.includes("under");
    const hasOver = marketType.includes("over") || selection.includes("over");
    const hasHome = marketType.includes("home") || selection.includes("home");
    const hasValue = marketType.includes(value) || selection.includes(value);

    return hasUnder && !hasOver && hasHome && hasValue;
  };

  const isUnderAway = (bet, value) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const hasUnder =
      marketType.includes("under") || selection.includes("under");
    const hasOver = marketType.includes("over") || selection.includes("over");
    const hasAway = marketType.includes("away") || selection.includes("away");
    const hasValue = marketType.includes(value) || selection.includes(value);

    return hasUnder && !hasOver && hasAway && hasValue;
  };

  const isBts = (bet) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const btsKeywords = ["both", "bts", "gg"];
    return (
      btsKeywords.some(
        (keyword) =>
          marketType.includes(keyword) || selection.includes(keyword),
      ) &&
      (marketType.includes("yes") || selection.includes("yes"))
    );
  };

  const isMatchWinner = (bet) => {
    const marketType = (bet.market_type || bet.marketType || "").toLowerCase();
    const selection = (bet.selection || bet.displayName || "").toLowerCase();

    const winnerKeywords = ["winner", "win", "1x2"];
    return winnerKeywords.some(
      (keyword) => marketType.includes(keyword) || selection.includes(keyword),
    );
  };

  // Funcții pentru a număra evenimentele
  const countGoalsOver1_5 = () => {
    return valueBets.filter((bet) => isOverTotal(bet, "1.5")).length;
  };

  const countGoalsOver2_5 = () => {
    return valueBets.filter((bet) => isOverTotal(bet, "2.5")).length;
  };

  const countGoalsOverHome0_5 = () => {
    return valueBets.filter((bet) => isOverHome(bet, "0.5")).length;
  };

  const countGoalsOverHome1_5 = () => {
    return valueBets.filter((bet) => isOverHome(bet, "1.5")).length;
  };

  const countGoalsOverAway0_5 = () => {
    return valueBets.filter((bet) => isOverAway(bet, "0.5")).length;
  };

  const countGoalsOverAway1_5 = () => {
    return valueBets.filter((bet) => isOverAway(bet, "1.5")).length;
  };

  const countGoalsUnder2_5 = () => {
    return valueBets.filter((bet) => isUnderTotal(bet, "2.5")).length;
  };

  const countGoalsUnder3_5 = () => {
    return valueBets.filter((bet) => isUnderTotal(bet, "3.5")).length;
  };

  const countGoalsUnder4_5 = () => {
    return valueBets.filter((bet) => isUnderTotal(bet, "4.5")).length;
  };

  const countGoalsUnderHome2_5 = () => {
    return valueBets.filter((bet) => isUnderHome(bet, "2.5")).length;
  };

  const countGoalsUnderHome3_5 = () => {
    return valueBets.filter((bet) => isUnderHome(bet, "3.5")).length;
  };

  const countGoalsUnderAway2_5 = () => {
    return valueBets.filter((bet) => isUnderAway(bet, "2.5")).length;
  };

  const countGoalsUnderAway3_5 = () => {
    return valueBets.filter((bet) => isUnderAway(bet, "3.5")).length;
  };

  const countBts = () => {
    return valueBets.filter((bet) => isBts(bet)).length;
  };

  const countMatchWinner = () => {
    return valueBets.filter((bet) => isMatchWinner(bet)).length;
  };

  // Funcția de sortare
  const sortBets = (bets) => {
    return [...bets].sort((a, b) => {
      if (sortBy === "time") {
        const timeA = a.match_date || a.date;
        const timeB = b.match_date || b.date;

        if (!timeA && !timeB) return 0;
        if (!timeA) return 1;
        if (!timeB) return -1;

        const dateA = new Date(timeA);
        const dateB = new Date(timeB);
        return dateA - dateB;
      } else if (sortBy === "value") {
        const marginA = a.calculatedMargin || 0;
        const marginB = b.calculatedMargin || 0;
        return marginB - marginA;
      }
      return 0;
    });
  };

  // Funcție pentru a verifica dacă un bet se potrivește cu filtrul
  const matchesFilter = (bet) => {
    // Status filter
    if (selectedStatus !== "all") {
      if (selectedStatus === "upcoming") {
        if (!isMatchUpcoming(bet)) return false;
      } else {
        const betStatus = bet.outcome || bet.status;
        if (betStatus !== selectedStatus) return false;
      }
    }

    // Verificăm dacă avem filtre active
    const hasGoalsOverFilter =
      selectedGoalsOver.over1_5 ||
      selectedGoalsOver.over2_5 ||
      selectedGoalsOver.overHome.over0_5 ||
      selectedGoalsOver.overHome.over1_5 ||
      selectedGoalsOver.overAway.over0_5 ||
      selectedGoalsOver.overAway.over1_5;

    const hasGoalsUnderFilter =
      selectedGoalsUnder.under2_5 ||
      selectedGoalsUnder.under3_5 ||
      selectedGoalsUnder.under4_5 ||
      selectedGoalsUnder.underHome.under2_5 ||
      selectedGoalsUnder.underHome.under3_5 ||
      selectedGoalsUnder.underAway.under2_5 ||
      selectedGoalsUnder.underAway.under3_5;

    const hasBtsFilter = selectedBts;
    const hasMatchWinnerFilter = selectedMatchWinner;

    if (
      !hasGoalsOverFilter &&
      !hasGoalsUnderFilter &&
      !hasBtsFilter &&
      !hasMatchWinnerFilter
    ) {
      return true;
    }

    let matchesAnyCategory = false;

    // Verifică Goals Over
    if (hasGoalsOverFilter) {
      if (selectedGoalsOver.over1_5 && isOverTotal(bet, "1.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsOver.over2_5 && isOverTotal(bet, "2.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsOver.overHome.over0_5 && isOverHome(bet, "0.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsOver.overHome.over1_5 && isOverHome(bet, "1.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsOver.overAway.over0_5 && isOverAway(bet, "0.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsOver.overAway.over1_5 && isOverAway(bet, "1.5")) {
        matchesAnyCategory = true;
      }
    }

    // Verifică Goals Under
    if (hasGoalsUnderFilter && !matchesAnyCategory) {
      if (selectedGoalsUnder.under2_5 && isUnderTotal(bet, "2.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsUnder.under3_5 && isUnderTotal(bet, "3.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsUnder.under4_5 && isUnderTotal(bet, "4.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsUnder.underHome.under2_5 && isUnderHome(bet, "2.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsUnder.underHome.under3_5 && isUnderHome(bet, "3.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsUnder.underAway.under2_5 && isUnderAway(bet, "2.5")) {
        matchesAnyCategory = true;
      }
      if (selectedGoalsUnder.underAway.under3_5 && isUnderAway(bet, "3.5")) {
        matchesAnyCategory = true;
      }
    }

    // Verifică Both Teams Score
    if (hasBtsFilter && !matchesAnyCategory) {
      if (isBts(bet)) {
        matchesAnyCategory = true;
      }
    }

    // Verifică Match Winner
    if (hasMatchWinnerFilter && !matchesAnyCategory) {
      if (isMatchWinner(bet)) {
        matchesAnyCategory = true;
      }
    }

    return matchesAnyCategory;
  };

  const loadValueBets = async (dayOffset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint =
        dayOffset < 0 ? "/premium/history" : "/premium/value-bets";
      const res = await fetch(`${API_URL}${endpoint}?day=${dayOffset}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load");

      const bets = (data.valueBets || []).map((bet) => ({
        ...bet,
        odd:
          typeof bet.odd === "string"
            ? parseFloat(bet.odd)
            : typeof bet.odd === "number"
              ? bet.odd
              : 0,
        winProbability:
          typeof bet.winProbability === "string"
            ? parseFloat(bet.winProbability)
            : typeof bet.winProbability === "number"
              ? bet.winProbability
              : 0,
        valueBetScore:
          typeof bet.valueBetScore === "string"
            ? parseFloat(bet.valueBetScore)
            : typeof bet.valueBetScore === "number"
              ? bet.valueBetScore
              : 0,
        margin:
          typeof bet.margin === "string"
            ? parseFloat(bet.margin)
            : typeof bet.margin === "number"
              ? bet.margin
              : 0,
        breakEvenProbability:
          typeof bet.breakEvenProbability === "string"
            ? parseFloat(bet.breakEvenProbability)
            : typeof bet.breakEvenProbability === "number"
              ? bet.breakEvenProbability
              : 0,
        home_team: bet.homeTeam || bet.home_team,
        away_team: bet.awayTeam || bet.away_team,
        league: bet.league,
        match_date: bet.date || bet.match_date,
        market_type: bet.marketType || bet.market_type,
        selection: bet.displayName || bet.selection,
        win_probability: bet.winProbability || bet.win_probability,
        outcome: bet.outcome,
        goals_home: bet.goals_home,
        goals_away: bet.goals_away,
        match_status: bet.match_status,
        country: bet.country || "Unknown Country",
        matchId: bet.matchId || bet.match_id,
      }));

      const betsWithMargin = bets.map((bet) => {
        const margin = calculateMargin(
          bet.odd,
          bet.win_probability || bet.winProbability,
        );
        return {
          ...bet,
          calculatedMargin: parseFloat(margin),
        };
      });

      const sortedBets = [...betsWithMargin].sort(
        (a, b) => b.calculatedMargin - a.calculatedMargin,
      );

      // Resetăm filtrele
      setSelectedMarket("all");
      setSelectedStatus("all");
      setSelectedGoalsOver({
        over1_5: false,
        over2_5: false,
        overHome: { over0_5: false, over1_5: false },
        overAway: { over0_5: false, over1_5: false },
      });
      setSelectedGoalsUnder({
        under2_5: false,
        under3_5: false,
        under4_5: false,
        underHome: { under2_5: false, under3_5: false },
        underAway: { under2_5: false, under3_5: false },
      });
      setSelectedBts(false);
      setSelectedMatchWinner(false);

      const upcomingCount = sortedBets.filter((bet) =>
        isMatchUpcoming(bet),
      ).length;
      setUpcomingMatchesCount(upcomingCount);

      setValueBets(sortedBets);

      // Aplică filtrarea și sortarea inițială
      let filtered = sortedBets.filter((bet) => matchesFilter(bet));
      filtered = sortBets(filtered);
      setFilteredBets(filtered);

      const won = sortedBets.filter((bet) => bet.outcome === "won").length;
      const lost = sortedBets.filter((bet) => bet.outcome === "lost").length;
      setDayStats({ total: sortedBets.length, won, lost });
    } catch (err) {
      console.error("Premium load error:", err);
      setError(err.message || "Failed to load analysis");
      setValueBets([]);
      setFilteredBets([]);
      setDayStats({ total: 0, won: 0, lost: 0 });
      setUpcomingMatchesCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Efect pentru filtrare și sortare
  useEffect(() => {
    let filtered = valueBets.filter((bet) => matchesFilter(bet));
    filtered = sortBets(filtered);
    setFilteredBets(filtered);
  }, [
    selectedMarket,
    selectedStatus,
    selectedGoalsOver,
    selectedGoalsUnder,
    selectedBts,
    selectedMatchWinner,
    valueBets,
    sortBy,
  ]);

  useEffect(() => {
    loadValueBets(selectedDayOffset);
  }, [selectedDayOffset]);

  const generateDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    let days = [];
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const adjustedFirstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;

    for (let i = 0; i < adjustedFirstDayOfWeek - 1; i++) {
      days.push(null);
    }

    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const formattedDate = `${year}-${month + 1}-${day}`;
      days.push(formattedDate);
    }

    return days;
  };

  const getDayOffsetFromDate = (dateString) => {
    if (!dateString) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [year, month, day] = dateString.split("-").map(Number);
    const dateObj = new Date(year, month - 1, day);
    dateObj.setHours(0, 0, 0, 0);

    const diffTime = dateObj - today;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= MIN_DAY_OFFSET && diffDays <= MAX_DAY_OFFSET) {
      return diffDays;
    }
    return null;
  };

  const handleDateClick = (date) => {
    if (!date) return;
    const offset = getDayOffsetFromDate(date);
    if (offset !== null) {
      setSelectedDayOffset(offset);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const toggleMenu = (menu) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const resetFilters = () => {
    setSelectedMarket("all");
    setSelectedStatus("all");
    setSelectedGoalsOver({
      over1_5: false,
      over2_5: false,
      overHome: { over0_5: false, over1_5: false },
      overAway: { over0_5: false, over1_5: false },
    });
    setSelectedGoalsUnder({
      under2_5: false,
      under3_5: false,
      under4_5: false,
      underHome: { under2_5: false, under3_5: false },
      underAway: { under2_5: false, under3_5: false },
    });
    setSelectedBts(false);
    setSelectedMatchWinner(false);
  };

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const PremiumPredictionCard = ({ bet, index, showUpgradeOverlay }) => {
    const margin = calculateMargin(
      bet.odd,
      bet.win_probability || bet.winProbability,
    );
    const breakEven = calculateBreakEven(bet.odd);
    const displayValueScore = Math.abs(margin);
    const finalHomeGoals = bet.goals_home;
    const finalAwayGoals = bet.goals_away;
    const matchOutcome = determineMatchOutcome(bet);

    const hasFinalResult =
      finalHomeGoals !== null &&
      finalHomeGoals !== undefined &&
      finalAwayGoals !== null &&
      finalAwayGoals !== undefined;

    const isMatchFinished =
      bet.match_status &&
      ["FT", "AET", "PEN", "FT_PEN"].includes(bet.match_status);

    const getValueScoreBarColor = () => {
      if (margin >= 15) return "bg-green-500";
      if (margin >= 10) return "bg-green-400";
      if (margin >= 5) return "bg-yellow-400";
      return "bg-yellow-300";
    };

    const homeTeam = bet.home_team || bet.homeTeam || "Home";
    const awayTeam = bet.away_team || bet.awayTeam || "Away";
    const league = bet.league || "Unknown";
    const country = bet.country || "Unknown Country";
    const matchTime =
      bet.match_date || bet.date ? formatTime(bet.match_date || bet.date) : "";
    const marketType = bet.market_type || bet.marketType || "Market";
    const selection = bet.selection || bet.displayName || "Selection";
    const odd = bet.odd
      ? typeof bet.odd === "number"
        ? bet.odd.toFixed(2)
        : bet.odd
      : "0.00";
    const winProbability = bet.win_probability || bet.winProbability || 0;
    const cardBorder = getCardBorder(matchOutcome, bet.status);

    const renderCenterContent = () => {
      if (isMatchFinished && hasFinalResult) {
        return (
          <>
            <div className="text-sm sm:text-base font-bold text-green-400 whitespace-nowrap">
              {finalHomeGoals} - {finalAwayGoals}
            </div>
            <div className="text-[10px] text-gray-500">
              {bet.match_status || "FT"}
            </div>
          </>
        );
      } else {
        return (
          <div className="px-2 py-1 bg-gray-600 rounded">
            <span className="text-xs font-bold text-purple-400">VS</span>
          </div>
        );
      }
    };

    const handleCardClick = () => {
      if (showUpgradeOverlay) return;
      if (bet.matchId) {
        navigate(`/match/${bet.matchId}`);
      }
    };

    return (
      <div
        className={`rounded-lg border p-3 mb-2 hover:shadow-md transition-all bg-gray-700 ${cardBorder} ${
          showUpgradeOverlay ? "relative" : "cursor-pointer"
        }`}
        onClick={handleCardClick}
      >
        {showUpgradeOverlay && (
          <div className="absolute inset-0 bg-black/50 rounded-lg z-10 flex items-center justify-center">
            <div className="text-center p-4">
              <div className="text-green-400 font-bold mb-2">
                Upgrade Required
              </div>
              <p className="text-green-300 text-sm">
                Upgrade to Premium to see all predictions
              </p>
            </div>
          </div>
        )}

        <div className={showUpgradeOverlay ? "blur-sm" : ""}>
          <div className="grid grid-cols-3 items-center mb-3 text-green-400">
            <div className="text-left">
              <div className="text-sm font-semibold truncate pr-2 text-green-300/70">
                {country}
              </div>
              <div className="text-[10px] font-bold truncate pr-2 text-green-400">
                {league}
              </div>
            </div>

            <div className="text-center flex justify-center">
              {getStatusBadge(bet.status, matchOutcome)}
            </div>

            <div className="text-right flex items-center justify-end gap-1">
              <span>⏰</span>
              <span className="text-xs text-green-300">{matchTime}</span>
            </div>
          </div>

          <div className="mb-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 pr-2">
                <div className="text-sm font-semibold text-purple-300 truncate text-right">
                  {homeTeam}
                </div>
              </div>

              <div className="flex-shrink-0 w-12 sm:w-16 mx-1 flex flex-col items-center">
                {renderCenterContent()}
              </div>

              <div className="flex-1 min-w-0 pl-2">
                <div className="text-sm font-semibold text-purple-300 truncate text-left">
                  {awayTeam}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-center mb-1">
              <div className="text-sm font-semibold text-green-300">
                {marketType}
              </div>
              <div className="text-sm font-bold text-purple-400 mt-1">
                {selection}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
              <div className="text-xs text-gray-400">Market Odds</div>
              <div className="text-sm font-bold text-purple-400">{odd}</div>
            </div>
            <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
              <div className="text-xs text-gray-400">Model Probability</div>
              <div className="text-sm font-bold text-purple-400">
                {winProbability}%
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="font-semibold text-gray-400">VALUE EDGE</span>
              <span className="font-bold text-green-400">
                {displayValueScore.toFixed(1)}
              </span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`${getValueScoreBarColor()} h-full transition-all duration-300`}
                style={{ width: `${Math.min(100, displayValueScore * 4)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
              <div className="text-xs text-gray-400">MARGIN</div>
              <div
                className={`text-sm font-bold ${margin > 0 ? "text-green-400" : "text-red-400"}`}
              >
                {margin > 0 ? "+" : ""}
                {margin}%
              </div>
            </div>
            <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
              <div className="text-xs text-gray-400">BREAK-EVEN</div>
              <div className="text-sm font-bold text-purple-400">
                {breakEven}%
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="bg-gray-800 rounded-lg border border-gray-700 p-4 animate-pulse"
        >
          <div className="flex justify-between mb-3">
            <div className="h-4 bg-gray-600 rounded w-24"></div>
            <div className="h-4 bg-gray-600 rounded w-16"></div>
          </div>
          <div className="flex justify-between mb-4">
            <div className="h-6 bg-gray-600 rounded w-20"></div>
            <div className="h-6 bg-gray-600 rounded w-8"></div>
            <div className="h-6 bg-gray-600 rounded w-20"></div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-600 rounded w-32"></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-10 bg-gray-600 rounded"></div>
              <div className="h-10 bg-gray-600 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const getVisibleCardCount = () => {
    if (userTier === "premium") {
      return filteredBets.length;
    } else {
      return Math.min(1, filteredBets.length);
    }
  };

  const visibleCardCount = getVisibleCardCount();
  const hasHiddenCards = filteredBets.length > visibleCardCount;

  // Componentă pentru meniu filtru simplu
  const SimpleFilterItem = ({ label, count, isActive, onClick }) => (
    <div
      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer ${
        isActive
          ? "bg-green-600 text-white"
          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
      }`}
      onClick={onClick}
    >
      <span className="text-sm">{label}</span>
      <span
        className={`text-xs px-2 py-1 rounded-full ${isActive ? "bg-green-700" : "bg-gray-600"}`}
      >
        {count}
      </span>
    </div>
  );

  // Componentă pentru checkbox
  const CheckboxOption = ({ label, count, checked, onChange }) => (
    <label className="flex items-center justify-between p-2 hover:bg-gray-700 rounded cursor-pointer">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="rounded border-gray-500 text-green-400 focus:ring-green-400"
        />
        <span className="text-sm text-gray-300">{label}</span>
      </div>
      <span className="text-xs bg-gray-700 px-2 py-1 rounded-full">
        {count}
      </span>
    </label>
  );

  // Componentă pentru radio
  const RadioOption = ({
    name,
    value,
    currentValue,
    onChange,
    label,
    count,
  }) => (
    <label className="flex items-center justify-between p-2 hover:bg-gray-700 rounded cursor-pointer">
      <div className="flex items-center gap-2">
        <input
          type="radio"
          name={name}
          value={value}
          checked={currentValue === value}
          onChange={(e) => onChange(e.target.value)}
          className="text-green-400 focus:ring-green-400"
        />
        <span className="text-sm text-gray-300">{label}</span>
      </div>
      {count !== undefined && (
        <span className="text-xs bg-gray-700 px-2 py-1 rounded-full">
          {count}
        </span>
      )}
    </label>
  );

  // Componentă pentru meniu expandabil
  const ExpandableMenu = ({
    title,
    icon,
    count,
    isExpanded,
    onToggle,
    children,
  }) => (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between p-3 bg-gray-700 cursor-pointer hover:bg-gray-600"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-green-400">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {count !== undefined && (
            <span className="text-xs bg-gray-600 px-2 py-1 rounded-full">
              {count}
            </span>
          )}
          {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </div>
      </div>
      {isExpanded && <div className="p-2 bg-gray-800">{children}</div>}
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-center mb-4 sm:mb-6 text-purple-400">
            Premium Analysis
          </h1>
          <p className="text-gray-400 mt-1 text-sm text-center mb-6">
            AI-powered algorithmic value events with an expected margin above
            break-even.
          </p>

          {/* Plan Indicator */}
          <div className="mb-6 flex justify-center">
            <div className="bg-gradient-to-r from-purple-900 to-purple-800 px-6 py-2 rounded-lg border border-purple-700">
              <span className="font-semibold text-purple-400">
                {userTier === "premium"
                  ? "Premium Plan - Full Access"
                  : "Pro Plan - Limited Access"}
              </span>
            </div>
          </div>

          {/* Grid principal */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Calendar - ocupă 2 coloane */}
            <div className="lg:col-span-2">
              <div
                ref={calendarRef}
                className="bg-gray-800 p-5 rounded-xl shadow-lg border border-gray-700"
              >
                <div className="flex justify-between items-center mb-6">
                  <button
                    className="text-xl sm:text-2xl text-green-400 hover:text-blue-500 transition duration-200"
                    onClick={goToPreviousMonth}
                  >
                    ←
                  </button>
                  <span className="text-lg sm:text-xl md:text-2xl font-medium text-green-400">
                    {currentDate.toLocaleString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <button
                    className="text-xl sm:text-2xl text-green-400 hover:text-blue-500 transition duration-200"
                    onClick={goToNextMonth}
                  >
                    →
                  </button>
                </div>

                {/* Zilele săptămânii */}
                <div className="grid grid-cols-7 gap-2 mb-3">
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      className="text-center font-medium text-green-400 text-sm sm:text-base py-2"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Zilele lunii */}
                <div className="grid grid-cols-7 gap-2">
                  {generateDays().map((date, index) => {
                    if (!date) {
                      return (
                        <div key={`empty-${index}`} className="p-2 sm:p-3" />
                      );
                    }

                    const offset = getDayOffsetFromDate(date);
                    const isSelectable = offset !== null;
                    const isSelected =
                      isSelectable && selectedDayOffset === offset;
                    const isToday = date === getTodayFormatted();

                    let bgColor = "bg-gray-700";
                    if (isSelectable) {
                      bgColor = "bg-gray-600";
                    }

                    return (
                      <div
                        key={index}
                        onClick={() => isSelectable && handleDateClick(date)}
                        className={`cursor-pointer p-2 sm:p-3 rounded-lg text-center text-sm sm:text-base min-h-[60px] flex flex-col justify-center items-center ${
                          isSelectable
                            ? bgColor
                            : "bg-gray-800/50 cursor-not-allowed opacity-50"
                        } ${isSelected ? "ring-2 ring-green-400" : ""} ${
                          isToday && !isSelected ? "ring-2 ring-yellow-400" : ""
                        }`}
                      >
                        <div>{date.split("-")[2]}</div>
                        {isToday && (
                          <div className="mt-1 text-xs text-yellow-300 font-bold">
                            TODAY
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Cele 4 carduri sub calendar */}
              <div className="grid grid-cols-4 gap-3 mt-4">
                <div className="bg-gray-800 p-3 rounded-xl shadow-lg border border-gray-700 text-center">
                  <div className="text-xs text-gray-400 mb-1">
                    Total Analysis
                  </div>
                  <div className="text-xl font-bold text-blue-400">
                    {dayStats.total}
                  </div>
                </div>

                <div className="bg-gray-800 p-3 rounded-xl shadow-lg border border-gray-700 text-center">
                  <div className="text-xs text-gray-400 mb-1">Won</div>
                  <div className="text-xl font-bold text-green-400">
                    {dayStats.won}
                  </div>
                </div>

                <div className="bg-gray-800 p-3 rounded-xl shadow-lg border border-gray-700 text-center">
                  <div className="text-xs text-gray-400 mb-1">Lost</div>
                  <div className="text-xl font-bold text-red-400">
                    {dayStats.lost}
                  </div>
                </div>

                <div className="bg-gray-800 p-3 rounded-xl shadow-lg border border-gray-700 text-center">
                  <div className="text-xs text-gray-400 mb-1">Upcoming</div>
                  <div className="text-xl font-bold text-yellow-400">
                    {upcomingMatchesCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Card filtru - ocupă 1 coloană în dreapta */}
            <div className="lg:col-span-1">
              <div className="bg-gray-800 p-4 rounded-xl shadow-lg border border-gray-700 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-green-400">
                    <FaFilter className="text-lg" />
                    <span className="font-semibold">Filters</span>
                  </div>
                  <button
                    onClick={resetFilters}
                    className="text-xs text-gray-400 hover:text-green-400 transition-colors"
                  >
                    Reset All
                  </button>
                </div>

                {/* Sortare - Butoane moderne */}
                <div className="mb-4 pb-3 border-b border-gray-700">
                  <div className="text-xs text-gray-400 mb-2">Sort by</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSortBy("time")}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        sortBy === "time"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      <span className="flex items-center justify-center gap-1">
                        ⏰ Time
                        {sortBy === "time" && (
                          <span className="text-xs">↑</span>
                        )}
                      </span>
                    </button>
                    <button
                      onClick={() => setSortBy("value")}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        sortBy === "value"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      <span className="flex items-center justify-center gap-1">
                        📊 Value Edge
                        {sortBy === "value" && (
                          <span className="text-xs">↓</span>
                        )}
                      </span>
                    </button>
                  </div>
                  <div className="text-center mt-2">
                    <span className="text-xs text-green-400/70">
                      {sortBy === "time"
                        ? "Sorted by earliest match time first"
                        : "Sorted by highest Value Edge first"}
                    </span>
                  </div>
                </div>

                {/* Filtrele */}
                <div
                  className="space-y-3 pr-1 overflow-y-auto scrollbar-thin scrollbar-track-gray-800/30 scrollbar-thumb-green-500 hover:scrollbar-thumb-green-400"
                  style={{ maxHeight: "calc(100vh - 380px)" }}
                >
                  {/* All Markets - simplu */}
                  <SimpleFilterItem
                    label="All Markets"
                    count={valueBets.length}
                    isActive={selectedMarket === "all"}
                    onClick={() => setSelectedMarket("all")}
                  />

                  {/* Status - cu radio */}
                  <ExpandableMenu
                    title="Status"
                    icon={<FaTicketAlt size={12} />}
                    count={valueBets.length}
                    isExpanded={expandedMenus.status}
                    onToggle={() => toggleMenu("status")}
                  >
                    <RadioOption
                      name="status"
                      value="all"
                      currentValue={selectedStatus}
                      onChange={setSelectedStatus}
                      label="All"
                      count={valueBets.length}
                    />
                    <RadioOption
                      name="status"
                      value="won"
                      currentValue={selectedStatus}
                      onChange={setSelectedStatus}
                      label="Won"
                      count={
                        valueBets.filter((b) => b.outcome === "won").length
                      }
                    />
                    <RadioOption
                      name="status"
                      value="lost"
                      currentValue={selectedStatus}
                      onChange={setSelectedStatus}
                      label="Lost"
                      count={
                        valueBets.filter((b) => b.outcome === "lost").length
                      }
                    />
                    <RadioOption
                      name="status"
                      value="upcoming"
                      currentValue={selectedStatus}
                      onChange={setSelectedStatus}
                      label="Upcoming"
                      count={upcomingMatchesCount}
                    />
                  </ExpandableMenu>

                  {/* Goals Over - cu checkbox-uri */}
                  <ExpandableMenu
                    title="Goals Over"
                    icon={<FaFutbol size={12} />}
                    count={
                      valueBets.filter(
                        (bet) =>
                          isOverTotal(bet, "1.5") ||
                          isOverTotal(bet, "2.5") ||
                          isOverHome(bet, "0.5") ||
                          isOverHome(bet, "1.5") ||
                          isOverAway(bet, "0.5") ||
                          isOverAway(bet, "1.5"),
                      ).length
                    }
                    isExpanded={expandedMenus.goalsOver}
                    onToggle={() => toggleMenu("goalsOver")}
                  >
                    <CheckboxOption
                      label="Over 1.5"
                      count={countGoalsOver1_5()}
                      checked={selectedGoalsOver.over1_5}
                      onChange={() =>
                        setSelectedGoalsOver({
                          ...selectedGoalsOver,
                          over1_5: !selectedGoalsOver.over1_5,
                        })
                      }
                    />
                    <CheckboxOption
                      label="Over 2.5"
                      count={countGoalsOver2_5()}
                      checked={selectedGoalsOver.over2_5}
                      onChange={() =>
                        setSelectedGoalsOver({
                          ...selectedGoalsOver,
                          over2_5: !selectedGoalsOver.over2_5,
                        })
                      }
                    />

                    {/* Over Home */}
                    <div className="ml-3 mt-2 border-l-2 border-gray-700 pl-2">
                      <div className="flex items-center gap-1 mb-2">
                        <FaHome size={12} className="text-green-400" />
                        <span className="text-xs text-green-400">
                          Over Home
                        </span>
                      </div>
                      <CheckboxOption
                        label="Over Home 0.5"
                        count={countGoalsOverHome0_5()}
                        checked={selectedGoalsOver.overHome.over0_5}
                        onChange={() =>
                          setSelectedGoalsOver({
                            ...selectedGoalsOver,
                            overHome: {
                              ...selectedGoalsOver.overHome,
                              over0_5: !selectedGoalsOver.overHome.over0_5,
                            },
                          })
                        }
                      />
                      <CheckboxOption
                        label="Over Home 1.5"
                        count={countGoalsOverHome1_5()}
                        checked={selectedGoalsOver.overHome.over1_5}
                        onChange={() =>
                          setSelectedGoalsOver({
                            ...selectedGoalsOver,
                            overHome: {
                              ...selectedGoalsOver.overHome,
                              over1_5: !selectedGoalsOver.overHome.over1_5,
                            },
                          })
                        }
                      />
                    </div>

                    {/* Over Away */}
                    <div className="ml-3 mt-2 border-l-2 border-gray-700 pl-2">
                      <div className="flex items-center gap-1 mb-2">
                        <FaPlane size={12} className="text-green-400" />
                        <span className="text-xs text-green-400">
                          Over Away
                        </span>
                      </div>
                      <CheckboxOption
                        label="Over Away 0.5"
                        count={countGoalsOverAway0_5()}
                        checked={selectedGoalsOver.overAway.over0_5}
                        onChange={() =>
                          setSelectedGoalsOver({
                            ...selectedGoalsOver,
                            overAway: {
                              ...selectedGoalsOver.overAway,
                              over0_5: !selectedGoalsOver.overAway.over0_5,
                            },
                          })
                        }
                      />
                      <CheckboxOption
                        label="Over Away 1.5"
                        count={countGoalsOverAway1_5()}
                        checked={selectedGoalsOver.overAway.over1_5}
                        onChange={() =>
                          setSelectedGoalsOver({
                            ...selectedGoalsOver,
                            overAway: {
                              ...selectedGoalsOver.overAway,
                              over1_5: !selectedGoalsOver.overAway.over1_5,
                            },
                          })
                        }
                      />
                    </div>
                  </ExpandableMenu>

                  {/* Goals Under - cu checkbox-uri */}
                  <ExpandableMenu
                    title="Goals Under"
                    icon={<FaFutbol size={12} />}
                    count={
                      valueBets.filter(
                        (bet) =>
                          isUnderTotal(bet, "2.5") ||
                          isUnderTotal(bet, "3.5") ||
                          isUnderTotal(bet, "4.5") ||
                          isUnderHome(bet, "2.5") ||
                          isUnderHome(bet, "3.5") ||
                          isUnderAway(bet, "2.5") ||
                          isUnderAway(bet, "3.5"),
                      ).length
                    }
                    isExpanded={expandedMenus.goalsUnder}
                    onToggle={() => toggleMenu("goalsUnder")}
                  >
                    <CheckboxOption
                      label="Under 2.5"
                      count={countGoalsUnder2_5()}
                      checked={selectedGoalsUnder.under2_5}
                      onChange={() =>
                        setSelectedGoalsUnder({
                          ...selectedGoalsUnder,
                          under2_5: !selectedGoalsUnder.under2_5,
                        })
                      }
                    />
                    <CheckboxOption
                      label="Under 3.5"
                      count={countGoalsUnder3_5()}
                      checked={selectedGoalsUnder.under3_5}
                      onChange={() =>
                        setSelectedGoalsUnder({
                          ...selectedGoalsUnder,
                          under3_5: !selectedGoalsUnder.under3_5,
                        })
                      }
                    />
                    <CheckboxOption
                      label="Under 4.5"
                      count={countGoalsUnder4_5()}
                      checked={selectedGoalsUnder.under4_5}
                      onChange={() =>
                        setSelectedGoalsUnder({
                          ...selectedGoalsUnder,
                          under4_5: !selectedGoalsUnder.under4_5,
                        })
                      }
                    />

                    {/* Under Home */}
                    <div className="ml-3 mt-2 border-l-2 border-gray-700 pl-2">
                      <div className="flex items-center gap-1 mb-2">
                        <FaHome size={12} className="text-green-400" />
                        <span className="text-xs text-green-400">
                          Under Home
                        </span>
                      </div>
                      <CheckboxOption
                        label="Under Home 2.5"
                        count={countGoalsUnderHome2_5()}
                        checked={selectedGoalsUnder.underHome.under2_5}
                        onChange={() =>
                          setSelectedGoalsUnder({
                            ...selectedGoalsUnder,
                            underHome: {
                              ...selectedGoalsUnder.underHome,
                              under2_5: !selectedGoalsUnder.underHome.under2_5,
                            },
                          })
                        }
                      />
                      <CheckboxOption
                        label="Under Home 3.5"
                        count={countGoalsUnderHome3_5()}
                        checked={selectedGoalsUnder.underHome.under3_5}
                        onChange={() =>
                          setSelectedGoalsUnder({
                            ...selectedGoalsUnder,
                            underHome: {
                              ...selectedGoalsUnder.underHome,
                              under3_5: !selectedGoalsUnder.underHome.under3_5,
                            },
                          })
                        }
                      />
                    </div>

                    {/* Under Away */}
                    <div className="ml-3 mt-2 border-l-2 border-gray-700 pl-2">
                      <div className="flex items-center gap-1 mb-2">
                        <FaPlane size={12} className="text-green-400" />
                        <span className="text-xs text-green-400">
                          Under Away
                        </span>
                      </div>
                      <CheckboxOption
                        label="Under Away 2.5"
                        count={countGoalsUnderAway2_5()}
                        checked={selectedGoalsUnder.underAway.under2_5}
                        onChange={() =>
                          setSelectedGoalsUnder({
                            ...selectedGoalsUnder,
                            underAway: {
                              ...selectedGoalsUnder.underAway,
                              under2_5: !selectedGoalsUnder.underAway.under2_5,
                            },
                          })
                        }
                      />
                      <CheckboxOption
                        label="Under Away 3.5"
                        count={countGoalsUnderAway3_5()}
                        checked={selectedGoalsUnder.underAway.under3_5}
                        onChange={() =>
                          setSelectedGoalsUnder({
                            ...selectedGoalsUnder,
                            underAway: {
                              ...selectedGoalsUnder.underAway,
                              under3_5: !selectedGoalsUnder.underAway.under3_5,
                            },
                          })
                        }
                      />
                    </div>
                  </ExpandableMenu>

                  {/* Both Teams Score - simplu ON/OFF */}
                  <SimpleFilterItem
                    label="Both Teams Score"
                    count={countBts()}
                    isActive={selectedBts}
                    onClick={() => setSelectedBts(!selectedBts)}
                  />

                  {/* Match Winner - simplu ON/OFF */}
                  <SimpleFilterItem
                    label="Match Winner"
                    count={countMatchWinner()}
                    isActive={selectedMatchWinner}
                    onClick={() => setSelectedMatchWinner(!selectedMatchWinner)}
                  />
                </div>

                {/* Rezumat filtre active */}
                <div className="mt-4 pt-3 border-t border-gray-700">
                  <div className="text-xs text-gray-400 text-center">
                    Showing {filteredBets.length} of {valueBets.length}{" "}
                    predictions
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-900/50 border border-red-500 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="text-red-400">⚠️</div>
                <div>
                  <h3 className="font-semibold text-red-400">Error</h3>
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Prediction Cards */}
          <div>
            {loading ? (
              <LoadingSkeleton />
            ) : filteredBets.length === 0 ? (
              <div className="text-center py-12 bg-gray-800 rounded-xl border border-gray-700">
                <FaTicketAlt className="text-4xl text-gray-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-green-400 mb-2">
                  No analysis available
                </h3>
                <p className="text-gray-400 max-w-md mx-auto text-sm">
                  {selectedDayOffset >= 0
                    ? `No published analysis for ${getDayDisplayName(selectedDayOffset)} yet.`
                    : `No historical analysis available for ${getDayDisplayName(selectedDayOffset)}.`}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredBets.slice(0, visibleCardCount).map((bet, i) => (
                    <PremiumPredictionCard
                      key={i}
                      bet={bet}
                      index={i}
                      showUpgradeOverlay={false}
                    />
                  ))}

                  {hasHiddenCards &&
                    filteredBets
                      .slice(visibleCardCount)
                      .map((bet, i) => (
                        <PremiumPredictionCard
                          key={i + visibleCardCount}
                          bet={bet}
                          index={i + visibleCardCount}
                          showUpgradeOverlay={true}
                        />
                      ))}
                </div>

                {/* Buton de upgrade */}
                {hasHiddenCards && userTier !== "premium" && (
                  <div className="mt-8 pt-8 border-t border-gray-700">
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-600 rounded-lg p-6 text-center">
                      <h3 className="text-xl font-bold text-green-400 mb-2">
                        {userTier === "pro"
                          ? "Upgrade to Premium for Full Access"
                          : "Upgrade to See All Predictions"}
                      </h3>
                      <p className="text-gray-300 mb-4 max-w-2xl mx-auto">
                        {userTier === "pro"
                          ? `Pro plan shows ${visibleCardCount} prediction. Upgrade to Premium to see all ${filteredBets.length} predictions.`
                          : `Your current plan shows only ${visibleCardCount} prediction. Upgrade to Premium to see all ${filteredBets.length} predictions.`}
                      </p>
                      <button
                        onClick={() => navigate("/subscription-plans")}
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        View Subscription Plans
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-center text-xs text-gray-500">
              AI-powered algorithmic value events with an expected margin above
              break-even.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
